import type { NotificationApi, NotificationFeed, RestaurantNotification } from './notificationTypes';

export interface NotificationIdentity { restaurantId: string; recipientUid: string; membershipId: string; accessKey: string }
export interface NotificationSnapshot { feed: NotificationFeed | null; loading: boolean; error: 'LOAD' | 'ACCESS' | 'READ' | null; marking: boolean }
export function feedBelongsTo(feed: NotificationFeed, identity: NotificationIdentity): boolean {
  return Boolean(feed && Object.entries(identity).every(([key, value]) => feed[key as keyof NotificationIdentity] === value)
    && Array.isArray(feed.notifications) && feed.notifications.length <= 200
    && Number.isFinite(Date.parse(feed.generatedAt)) && Number.isInteger(feed.unreadCount)
    && feed.unreadCount === feed.notifications.filter(entry => !entry.read).length);
}

/** One actor, one in-flight poll, no stale publication after disposal. No business mutations. */
export function createNotificationController(api: NotificationApi, identity: NotificationIdentity,
  publish: (state: NotificationSnapshot) => void, onNew: (entries: RestaurantNotification[]) => void) {
  let state: NotificationSnapshot = { feed: null, loading: true, error: null, marking: false };
  let disposed = false, blocked = false, request: Promise<void> | null = null, abort: AbortController | null = null;
  let initialized = false, watermark = 0, seen = new Set<string>(), readVersion = 0;
  const emit = () => { if (!disposed) publish({ ...state }); };
  const refresh = (): Promise<void> => {
    if (disposed || blocked) return Promise.resolve();
    if (request) return request;
    abort = new AbortController(); const version = readVersion;
    state = { ...state, loading: true }; emit();
    request = (async () => {
      try {
        const feed = await api.load(abort!.signal);
        if (disposed || blocked) return;
        if (!feedBelongsTo(feed, identity)) { blocked = true; state = { ...state, feed: null, error: 'ACCESS' }; return; }
        // A read completed during this GET: retain the acknowledged IDs until the next poll.
        if (version !== readVersion && state.feed) {
          const read = new Set(state.feed.notifications.filter(entry => entry.read).map(entry => entry.id));
          feed.notifications = feed.notifications.map(entry => read.has(entry.id) ? { ...entry, read: true } : entry);
          feed.unreadCount = feed.notifications.filter(entry => !entry.read).length;
        }
        const fresh = initialized ? feed.notifications.filter(entry => !entry.read && !seen.has(entry.id)
          && Date.parse(entry.occurredAt) > watermark && Date.parse(entry.occurredAt) <= Date.parse(feed.generatedAt)) : [];
        feed.notifications.forEach(entry => seen.add(entry.id));
        if (seen.size > 2000) seen = new Set([...seen].slice(-1000));
        // Keep the first-load baseline: a cached feed must not move the cutoff past an unseen event.
        if (!initialized) watermark = Date.parse(feed.generatedAt);
        initialized = true;
        state = { ...state, feed, error: null };
        if (fresh.length) onNew(fresh);
      } catch (error) {
        if (disposed || blocked || abort?.signal.aborted) return;
        const status = (error as { status?: number }).status;
        blocked = status === 401 || status === 403 || status === 402;
        state = { ...state, feed: blocked ? null : state.feed, error: blocked ? 'ACCESS' : 'LOAD' };
      } finally { request = null; if (!disposed) { state = { ...state, loading: false }; emit(); } }
    })();
    return request;
  };
  const markRead = async (ids: string[]): Promise<boolean> => {
    if (disposed || blocked || state.marking || !state.feed) return false;
    const allowed = new Set(state.feed.notifications.filter(entry => !entry.read).map(entry => entry.id));
    const selected = [...new Set(ids)].filter(id => allowed.has(id)).slice(0, 200);
    if (!selected.length) return true;
    state = { ...state, marking: true }; emit();
    try {
      const result = await api.markRead(selected);
      if (disposed || blocked) return false;
      if (!Array.isArray(result.readIds) || result.readIds.some(id => !selected.includes(id))) throw Error('Invalid read acknowledgement');
      readVersion++;
      const accepted = new Set(result.readIds);
      if (state.feed) {
        const notifications = state.feed.notifications.map(entry => accepted.has(entry.id) ? { ...entry, read: true } : entry);
        state = { ...state, feed: { ...state.feed, notifications, unreadCount: notifications.filter(entry => !entry.read).length }, error: null };
      }
      return true;
    } catch (error) {
      if (!disposed && !blocked) {
        const status = (error as { status?: number }).status;
        blocked = status === 401 || status === 402 || status === 403;
        state = { ...state, error: blocked ? 'ACCESS' : 'READ', feed: blocked ? null : state.feed };
      }
      return false;
    } finally { if (!disposed) { state = { ...state, marking: false }; emit(); } }
  };
  return { refresh, markRead, snapshot: () => state, dispose: () => { disposed = true; abort?.abort(); seen.clear(); } };
}
